import { GuideEditorPage } from '@/app/guide/GuideEditorPage';
import { GUIDE_DOTS } from '@/app/guide/guideDot';
import { GuidesTab } from '@/app/guide/GuidesTab';
import InfoTypesTable from '@/app/space/InfoTypesTable';
import SpaceDetails from '@/app/space/SpaceDetails';
import { PageHeader, PageShell } from '@/components/PageShell';
import { TabNav } from '@/components/TabNav';
import { infoTypesState, spaceState } from '@/state';
import { guideHealthState, guideRowsState } from '@/state/guide.state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { KEY_INFO_TITLE, type SpaceId } from '@rock/types';
import { type FC, useMemo } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

/**
 * One space: its details, its key-info types, and its guides. EDITING a guide is a sibling of the
 * tabbed shell rather than a pane inside it — the ReactFlow canvas wants the whole viewport, so it
 * renders full-bleed with its own back arrow.
 */
export const SpacePage: FC<{ spaceId: SpaceId }> = ({ spaceId }) => (
    <Routes>
        <Route path="guides/:flowId" element={<SpaceGuideEditor spaceId={spaceId} />} />
        <Route path="*" element={<SpaceTabs spaceId={spaceId} />} />
    </Routes>
);

const SpaceGuideEditor: FC<{ spaceId: SpaceId }> = ({ spaceId }) => {
    const { flowId } = useParams<{ flowId: string }>();
    const params = useSpaceFlowParams(spaceId);
    return <GuideEditorPage params={params} flowId={flowId} editBase={`/spaces/${spaceId}/guides`} showBack />;
};

const SpaceTabs: FC<{ spaceId: SpaceId }> = ({ spaceId }) => {
    const space = useAsyncAtomValue(spaceState(spaceId));
    const infoTypes = useAsyncAtomValue(infoTypesState(spaceId), []);
    const params = useSpaceFlowParams(spaceId);
    const guides = useAsyncAtomValue(guideRowsState(params), []);
    const health = useAsyncAtomValue(guideHealthState(params), 'none');
    const base = `/spaces/${spaceId}`;

    return (
        <PageShell>
            <PageHeader title={space?.name ?? spaceId} id={spaceId} />

            <TabNav
                tabs={[
                    { label: 'Details', to: `${base}/details` },
                    { label: KEY_INFO_TITLE, to: `${base}/info`, count: infoTypes.length },
                    { label: 'Guides', to: `${base}/guides`, count: guides.length, dot: GUIDE_DOTS[health] },
                ]}
            />

            <Routes>
                <Route index element={<Navigate replace to="./details" />} />
                <Route path="details" element={<SpaceDetails spaceId={spaceId} />} />
                <Route path="info" element={<InfoTypesTable spaceId={spaceId} />} />
                <Route path="guides" element={<GuidesTab params={params} editBase={`${base}/guides`} />} />
                {/* An unknown sub-path used to render the shell with an empty body. */}
                <Route path="*" element={<Navigate replace to={`${base}/details`} />} />
            </Routes>
        </PageShell>
    );
};

/** Stable params object — the flow atom families key on it, so a fresh literal each render thrashes them. */
function useSpaceFlowParams(spaceId: SpaceId) {
    return useMemo(() => ({ spaceId, equipmentTypeId: undefined }), [spaceId]);
}
